import { Component, signal, computed, effect, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { SearchBoxComponent } from '../../../shared/components/search-box/search-box.component';
import { SpinnerLoaderComponent } from '../../../shared/components/spinner-loader/spinner-loader.component';
import { IWaitlistUserReq, WaitlistContactMethod, WaitlistLeadSourceEnum } from '../../../Utils/_models/users.model';
import { AdminService } from '../../../Utils/_services/admin/admin/admin.service';
import { ConfirmService } from '../../../Utils/_services/confirmation-dialog.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-waitlisted-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SelectModule,
    SearchBoxComponent,
    SpinnerLoaderComponent,
    DatePipe
  ],
  templateUrl: './waitlisted-user.component.html',
  styleUrl: './waitlisted-user.component.css'
})

export class WaitlistedUserComponent implements OnInit {

  loading = signal(false);
  searchTerm = signal<string | null>(null);
  currentPage = signal(1);
  totalCount = signal(0);
private allUsers = signal<IWaitlistUserReq[]>([]);
users = signal<IWaitlistUserReq[]>([]);
selectedUser = signal<IWaitlistUserReq | null>(null);
showViewModal = signal(false);
  allSelected = signal(false);
  includeDeleted = signal(false);
  hasSelection = signal(false);
  hasDeletedSelection = signal(false);
  currentSortField = signal<string | null>(null);
  currentSortOrder = signal<'asc' | 'desc' | null>(null);
   pageSizes: number[] = [5, 10, 20, 50];
  pageSize = signal(0);
  totalPages: number = 0;
  startIndex: number = 1;
  endIndex: number = 10;
  private data = signal<IWaitlistUserReq[]>([]);
  contactMethodFilter = signal<number | null>(null);
  leadSourceFilter = signal<number | null>(null);
  contactMethods = [
    { label: 'Email', value: WaitlistContactMethod.Email },
    { label: 'Text', value: WaitlistContactMethod.Text }
  ];
  leadSources = [
    { label: 'Social Media', value: WaitlistLeadSourceEnum.SocialMedia },
    { label: 'Word of Mouth', value: WaitlistLeadSourceEnum.WordOfMouth },
    { label: 'Website', value: WaitlistLeadSourceEnum.Website },
    { label: 'Email Newsletter', value: WaitlistLeadSourceEnum.EmailNewsLetter },
    { label: 'Events', value: WaitlistLeadSourceEnum.Events },
    { label: 'Search Engines', value: WaitlistLeadSourceEnum.SearchEngines },
    { label: 'Other', value: WaitlistLeadSourceEnum.Other }
  ];

  constructor(
    private adminService: AdminService,
    private confirmationService: ConfirmService
  ) { }

  ngOnInit() {
    this.pageSize.set(this.pageSizes[0]);
    this.fetchUsers();
  }

fetchUsers(): void {
  this.allSelected.set(false);
  this.loading.set(true);
  this.adminService.getJoinWaitlist()
    .pipe(finalize(() => this.loading.set(false)))
    .subscribe({
      next: res => {
        this.allUsers.set(res.data.items ?? []);
        this.applyFilters();
      },
      error: () => {
        this.allUsers.set([]);
        this.totalCount.set(0);
      }
    });
}

applyFilters() {
  let items = [...this.allUsers()];

  if (!this.includeDeleted()) {
    items = items.filter(u => !u.deletedAt);
  }

  const term = (this.searchTerm() || '').toLowerCase();

  if (term) {
    items = items.filter(u =>
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(term) ||
      (u.fullName || '').toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term) ||
      (u.phone || '').toLowerCase().includes(term)
    );
  }
  if (this.contactMethodFilter() !== null) {
    items = items.filter(u => u.preferredContactMethod === this.contactMethodFilter());
  }
  if (this.leadSourceFilter() !== null) {
    items = items.filter(u => u.leadSource === this.leadSourceFilter());
  }

  // Apply sorting
  const field = this.currentSortField();
  const order = this.currentSortOrder();
  if (field && order) {
    const direction = order === 'asc' ? 1 : -1;
    items.sort((a: any, b: any) => {
      let valA: any = a[field];
      let valB: any = b[field];

      if (field === 'name') {
        const nameA = ((a.firstName || '') + ' ' + (a.lastName || '')).toLowerCase();
        const nameB = ((b.firstName || '') + ' ' + (b.lastName || '')).toLowerCase();
        valA = nameA;
        valB = nameB;
      } else if (field === 'email') {
        valA = (a.email || '').toLowerCase();
        valB = (b.email || '').toLowerCase();
      } else if (field === 'phone') {
        valA = (a.phone || '').toLowerCase();
        valB = (b.phone || '').toLowerCase();
      } else if (field === 'preferredContactMethod') {
        valA = Number(a.preferredContactMethod || 0);
        valB = Number(b.preferredContactMethod || 0);
      } else if (field === 'leadSource') {
        valA = Number(a.leadSource || 0);
        valB = Number(b.leadSource || 0);
      } else if (field === 'createdAt') {
        valA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        valB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      }

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });
  }

  this.totalCount.set(items.length);
  this.totalPages = Math.ceil(items.length / this.pageSize());
  this.startIndex = (this.currentPage() - 1) * this.pageSize() + 1;
  this.endIndex = Math.min(this.currentPage() * this.pageSize(), items.length);

  const start = (this.currentPage() - 1) * this.pageSize();
  this.users.set(items.slice(start, start + this.pageSize()));
  this.updateSelectAllState();
}

  updatePagination() {
    this.totalPages = Math.ceil(this.totalCount() / this.pageSize());
    this.startIndex = (this.currentPage() - 1) * this.pageSize() + 1;
    this.endIndex = Math.min(
      this.currentPage() * this.pageSize(),
      this.totalCount()
    );

    this.updateSelectAllState();
  }

  toggleIncludeDeleted() {
    this.includeDeleted.update(v => !v);
    this.applyFilters();
  }

  deleteSelectedUsers() {
    const selectedUsers = this.allUsers().filter(u => u.selected);
    if (!selectedUsers.length) return;

    const deletedSelected = selectedUsers.filter(u => u.deletedAt);
    const activeSelected = selectedUsers.filter(u => !u.deletedAt);
    const isRestore = deletedSelected.length > 0;

    const header = isRestore ? 'Restore Users' : 'Delete Users';
    const bodyHeader = isRestore ? 'Restore Waitlisted Users' : 'Delete Waitlisted Users';
    const bodySubtitle = isRestore
      ? `You are about to restore ${deletedSelected.length} deleted user(s).${activeSelected.length > 0 ? ` ${activeSelected.length} non-deleted user(s) will also be deleted.` : ''}`
      : `Are you sure you want to delete ${activeSelected.length} selected user(s)?`;
    const yesButtonText =
      deletedSelected.length > 0 && activeSelected.length > 0
        ? 'Apply Changes'
        : isRestore
          ? 'Restore'
          : 'Delete';
    const yesBtnCss = isRestore ? 'btn-primary' : 'btn-danger';

    this.confirmationService.confirm({
      header,
      bodyHeader,
      bodySubtitle,
      yesButtonText,
      yesBtnCss
    }).then(confirmed => {
      if (confirmed) {
        // Mock backend behavior (user will add API later)
        selectedUsers.forEach(u => {
          if (u.deletedAt) {
            u.deletedAt = undefined as any;
          } else {
            u.deletedAt = new Date().toISOString();
          }
          u.selected = false;
        });
        this.updateSelectAllState();
        this.applyFilters();
      }
    });
  }

  toggleSelectAll(): void {
    this.allSelected.update(v => !v);
    this.users().forEach(u => {
      u.selected = this.allSelected();
    });
    this.updateSelectAllState();
  }

  updateSelectAllState() {
    const allUsers = this.users();
    const selectedCount = allUsers.filter(user => user.selected).length;
    this.allSelected.set(
      selectedCount === allUsers.length && allUsers.length > 0
    );
    this.hasSelection.set(this.allUsers().some(u => u.selected));
    this.hasDeletedSelection.set(this.allUsers().some(u => u.selected && !!u.deletedAt));
  }

  // filteredItems() {
  //   let items = [];
  //   const term = (this.searchTerm() || '').toLowerCase();
  //   if (term) {
  //     items = items.filter(x =>
  //       `${x.firstName} ${x.lastName}`.toLowerCase().includes(term) ||
  //       x.email.toLowerCase().includes(term) ||
  //       (x.phone || '').toLowerCase().includes(term)
  //     );
  //   }
  //   if (this.contactMethodFilter() !== null) {
  //     items = items.filter(x => x.contactMethod === this.contactMethodFilter());
  //   }
  //   if (this.leadSourceFilter() !== null) {
  //     items = items.filter(x => x.leadSource === this.leadSourceFilter());
  //   }
  //   return items;
  // };

  // usersd(){
  //   const items = this.filteredItems();
  //   const start = (this.currentPage() - 1) * this.pageSize();
  //   const end = Math.min(start + this.pageSize(), items.length);
  //   return items.slice(start, end);
  // };




  toggleContacted(user: IWaitlistUserReq) {
    this.data.set(
      this.data().map(u =>
        u.email === user.email ? { ...u, contacted: !u.preferredContactMethod } : u
      )
    );
  }

  getPreferredContactMethod(value?: number) {
    switch(value) {
      case 1:
        return "Email"
      case 2:
        return "Text";
      case 3:
        return "Phone";
      case 4:
        return "PhoneCall";
      case 5:
        return "Fax";
      case 6:
        return "Mail";
      case 7:
        return "PushNotification";
      case 8:
        return "WhatsApp";
      case 9:
        return "InsizonAppPushNotification";
      case 10:
        return "GoogleMeetsVideoCall";
      default:
        return "N/A"
    }
  }

  getLeadSourceLabel(value?: number) {
    return this.leadSources.find(x => x.value === value)?.label || 'N/A';
  }

  onSearch(value: string | null) {
  this.searchTerm.set(value);
  this.currentPage.set(1);
  this.applyFilters(); 
}

onFilterChange() {
  this.currentPage.set(1);
  this.applyFilters(); 
}

onPageSizeChange(size: number) {
  this.pageSize.set(+size);
  this.currentPage.set(1);
  this.applyFilters(); 
}

nextPage() {
  if (this.currentPage() < this.totalPages) {
    this.currentPage.update(v => v + 1);
    this.applyFilters(); 
  }
}

previousPage() {
  if (this.currentPage() > 1) {
    this.currentPage.update(v => v - 1);
    this.applyFilters(); 
  }
}



viewUser(user: IWaitlistUserReq) {
  this.selectedUser.set(user);
  this.showViewModal.set(true);
}

  closeModal() {
    this.showViewModal.set(false);
    this.selectedUser.set(null);
  }

  onSort(field: string): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.applyFilters();
  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }
}
